import { ArrowBack, Clear, FilterList, History } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  List,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChatModel } from "../../data/models/Chat/ChatModel";
import {
  searchCompanyChatsByPhone,
  useChatHistory,
} from "../../data/providers/FirestoreProvider";
import { ChatItem } from "./ChatItem";
import { ChatEmptyState } from "./ChatEmptyState";

type ChatHistoryPanelProps = {
  company: string;
  selectedChat?: ChatModel;
  onBack: () => void;
  onSelectChat: (chat: ChatModel) => void;
};

type DatePreset = "today" | "yesterday" | "week" | "month" | null;

const datePresetOptions: Array<{
  value: Exclude<DatePreset, null>;
  label: string;
  description: string;
}> = [
  { value: "today", label: "Hoy", description: "Desde hoy" },
  { value: "yesterday", label: "Ayer", description: "Desde ayer" },
  { value: "week", label: "7 días", description: "Desde hace una semana" },
  { value: "month", label: "1 mes", description: "Desde hace un mes" },
];

const toInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRange = (preset: DatePreset, now = new Date()) => {
  if (!preset) return { from: "", to: "" };

  const from = new Date(now);
  if (preset === "yesterday") from.setDate(from.getDate() - 1);
  if (preset === "week") from.setDate(from.getDate() - 7);
  if (preset === "month") from.setMonth(from.getMonth() - 1);

  return {
    from: toInputDate(from),
    to: toInputDate(now),
  };
};

export const ChatHistoryPanel = ({
  company,
  selectedChat,
  onBack,
  onSelectChat,
}: ChatHistoryPanelProps) => {
  const { chats, loading, hasMore, error, loadMore } = useChatHistory(company, true);
  const listRef = useRef<HTMLUListElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [datePreset, setDatePreset] = useState<DatePreset>("week");
  const [assignment, setAssignment] = useState<"all" | "ai" | "agent">("all");
  const [phoneResults, setPhoneResults] = useState<ChatModel[] | null>(null);
  const [phoneSearchLoading, setPhoneSearchLoading] = useState(false);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const searchDigits = normalizedSearch.replace(/\D/g, "");
  const { from: fromDate, to: toDate } = useMemo(
    () => getDateRange(datePreset),
    [datePreset]
  );

  useEffect(() => {
    let cancelled = false;
    if (searchDigits.length < 4) {
      setPhoneResults(null);
      setPhoneSearchLoading(false);
      return;
    }
    setPhoneSearchLoading(true);
    void searchCompanyChatsByPhone(company, searchDigits)
      .then((results) => {
        if (!cancelled) setPhoneResults(results);
      })
      .catch(() => {
        if (!cancelled) setPhoneResults([]);
      })
      .finally(() => {
        if (!cancelled) setPhoneSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [company, searchDigits]);

  const visibleChats = useMemo(() => {
    const digits = normalizedSearch.replace(/\D/g, "");
    const sourceChats = phoneResults || chats;
    return sourceChats.filter((chat) => {
      const activity = chat.lastMessageDate || chat.lastInboundMessageDate || chat.updatedAt || chat.createdAt;
      const activityDate = activity instanceof Date && !Number.isNaN(activity.getTime())
        ? toInputDate(activity)
        : "";
      const name = String(chat.displayName || chat.fullName || "").toLowerCase();
      const phone = String(chat.phoneNumber || "");
      const preview = String(chat.lastMessage || "").toLowerCase();
      const textMatches = !normalizedSearch || (
        name.includes(normalizedSearch) ||
        preview.includes(normalizedSearch) ||
        phone.toLowerCase().includes(normalizedSearch) ||
        (digits && phone.replace(/\D/g, "").includes(digits))
      );
      const dateMatches = (!fromDate || activityDate >= fromDate) && (!toDate || activityDate <= toDate);
      const assignmentMatches = assignment === "all" || (assignment === "ai" ? chat.botIsEnabled : !chat.botIsEnabled);
      return Boolean(textMatches && dateMatches && assignmentMatches);
    });
  }, [assignment, chats, fromDate, normalizedSearch, phoneResults, toDate]);

  const clearFilters = () => {
    setSearchTerm("");
    setDatePreset(null);
    setAssignment("all");
    setFiltersOpen(false);
  };
  // Las fechas son filtros normales del historial y no deben desactivar la
  // paginación: al bajar se deben seguir cargando más páginas.
  useEffect(() => {
    const root = listRef.current;
    const target = loadMoreRef.current;
    if (!root || !target || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !loading) {
          void loadMore();
        }
      },
      { root, rootMargin: "0px 0px 240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={onBack}>
          Volver a activos
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          <History fontSize="inherit" /> Historial
        </Typography>
      </Box>
      <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
        <Stack direction="row" spacing={0.75}>
          <TextField
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Nombre, número o mensaje"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
          />
          <Button
            variant={filtersOpen ? "contained" : "outlined"}
            onClick={() => setFiltersOpen((current) => !current)}
            sx={{ minWidth: 44, borderRadius: 999, px: 1 }}
            aria-label="Abrir filtros del historial"
          >
            <FilterList fontSize="small" />
          </Button>
        </Stack>
        {filtersOpen && (
          <Stack spacing={0.75} mt={0.75}>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ width: "100%" }}
            >
              {datePresetOptions.map((option) => (
                <Button
                  key={option.value}
                  size="small"
                  variant={datePreset === option.value ? "contained" : "outlined"}
                  onClick={() => setDatePreset(option.value)}
                  aria-pressed={datePreset === option.value}
                  aria-label={option.description}
                  title={option.description}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 0.75,
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    minHeight: 38,
                    borderRadius: 999,
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" spacing={0.75}>
              <TextField
                select
                size="small"
              label="Atención"
                value={assignment}
                onChange={(event) => setAssignment(event.target.value as typeof assignment)}
                sx={{ flex: 1 }}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="ai">IA</MenuItem>
                <MenuItem value="agent">Agente</MenuItem>
              </TextField>
              <Button onClick={clearFilters} sx={{ minWidth: 44 }} aria-label="Limpiar filtros">
                <Clear fontSize="small" />
              </Button>
            </Stack>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {visibleChats.length} resultados
        </Typography>
      </Box>
      <List ref={listRef} disablePadding sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {error ? (
          <Typography color="error" align="center" sx={{ py: 4, px: 2 }}>
            No se pudo cargar el historial. Verifica que el índice de Firestore esté listo.
          </Typography>
        ) : phoneSearchLoading ? (
          <ChatEmptyState
            loading
            icon={<History sx={{ fontSize: 30 }} />}
            title="Buscando conversaciones"
            description="Estamos revisando el historial."
          />
        ) : loading && chats.length === 0 ? (
          <Box sx={{ minHeight: 260 }}>
            <ChatEmptyState
              loading
              icon={<History sx={{ fontSize: 30 }} />}
              title="Cargando el historial"
              description="Estamos preparando tus conversaciones."
            />
          </Box>
        ) : visibleChats.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4, px: 2 }}>
            {normalizedSearch
              ? "No encontramos chats que coincidan con la búsqueda."
            : "Todavía no hay conversaciones en el historial."}
          </Typography>
        ) : (
          visibleChats.map((chat) => (
            <ChatItem
              key={chat.phoneNumber}
              chat={chat}
              selectedChat={selectedChat}
              handleSelectChat={onSelectChat}
              handleOpenProfile={onSelectChat}
            />
          ))
        )}
        <Box ref={loadMoreRef} display="flex" justifyContent="center" py={1.5}>
          {loading && chats.length > 0 && <CircularProgress size={20} />}
        </Box>
      </List>
    </Box>
  );
};
